import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createResource,getResources,deleteResource, updateResource} from '../../router/api';

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await createResource(`forgot-password`, {
        email: email.trim(),
      });

      setSuccess(
        response.data.message ||
          'Un email de réinitialisation a été envoyé à votre adresse.'
      );
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        'Une erreur est survenue. Vérifiez votre adresse email.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo et Titre – inchangé */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <i className="ri-building-2-fill text-white text-3xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">MAI MAB SERVICES</h1>
          <p className="text-gray-600">Réinitialisation du mot de passe</p>
        </div>

        {/* Card formulaire – on garde exactement les mêmes classes */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-unlock-line text-blue-600 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié ?</h2>
            <p className="text-gray-600 text-sm">
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
          </div>

          {/* Messages d'erreur / succès – on garde le style exact */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <i className="ri-error-warning-line text-red-600"></i>
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
              <i className="ri-check-line text-green-600"></i>
              <span className="text-sm text-green-600">{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Champ email avec floating label (amélioration UX sans casser le design) */}
            <div className="relative">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className={`
                  w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg 
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                  transition-all duration-200
                  ${email || isFocused ? 'pt-5 pb-1' : 'py-3'}
                `}
                placeholder={email || isFocused ? '' : 'votre@email.com'}
                required
                autoComplete="email"
              />

              <label
                htmlFor="email"
                className={`
                  absolute left-10 text-sm font-medium text-gray-700 transition-all duration-200 pointer-events-none
                  ${email || isFocused
                    ? '-top-2 text-xs text-blue-600'
                    : 'top-3.5 text-sm text-gray-700'}
                `}
              >
              </label>

              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className={`ri-mail-line ${email || isFocused ? 'text-blue-500' : 'text-gray-400'}`}></i>
              </div>
            </div>

            {/* Bouton – style identique, juste un peu plus fluide */}
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className={`
                w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 
                rounded-lg font-medium transition-all duration-200 shadow-lg 
                disabled:opacity-50 disabled:cursor-not-allowed 
                disabled:hover:bg-blue-600 flex items-center justify-center gap-2
              `}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Envoi en cours...</span>
                </>
              ) : (
                'Envoyer le lien de réinitialisation'
              )}
            </button>
          </form>

          {/* Lien retour – inchangé */}
          <div className="mt-6 text-center space-y-2">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
            >
              ← Retour à la connexion
            </button>
          </div>
        </div>

        {/* Footer – mise à jour année dynamique */}
        <p className="mt-6 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} MAI MAB SERVICES - Tous droits réservés
        </p>
      </div>
    </div>
  );
}